import { ArrowUpRight, Plus, RotateCw } from "lucide-react";
import { type Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Button, Label, SearchableMultiSelect } from "~/components/ui";
import type { BatchChangeRequestJobsFormData } from "~/features/dataImportProjects/types";
import { v2Route } from "~/v2/routes";

interface ComposerDefinitionOption {
  id: string;
  name: string;
}

interface BatchChangeRequestsComposerFieldProps {
  control: Control<BatchChangeRequestJobsFormData>;
  importProjectId: string;
  label: string;
  /** Already filtered by the caller — entity and relation composers differ on what they hide. */
  options: ComposerDefinitionOption[];
  peekType: "entityComposer" | "relationComposer";
  hasEvidenceSchemaSet: boolean;
  defaultValue?: string[];
  onRefetch: () => void;
  onAdd: () => void;
  setValue: (name: "name", value: string) => void;
}

export function BatchChangeRequestsComposerField({
  control,
  importProjectId,
  label,
  options,
  peekType,
  hasEvidenceSchemaSet,
  defaultValue,
  onRefetch,
  onAdd,
  setValue,
}: BatchChangeRequestsComposerFieldProps) {
  const { t } = useTranslation();

  return (
    <Controller
      name="recordComposerDefinitionIds"
      control={control}
      defaultValue={defaultValue ?? []}
      render={({ field }) => {
        const selectedIds = options.filter((def) => field.value.includes(def.id)).map((def) => def.id);
        const selectedDefs = options.filter((def) => selectedIds.includes(def.id));

        return (
          <div className="space-y-2">
            <Label>{label}</Label>
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <SearchableMultiSelect
                  placeholder={label}
                  searchPlaceholder={t("ui.search")}
                  emptyText={t("feature.common.noData")}
                  options={options.map((def) => ({ id: def.id, label: def.name }))}
                  selectedIds={selectedIds}
                  onChange={(ids) => {
                    field.onChange(ids);
                    // The job inherits its name from the first pick, matching how these jobs are named by hand.
                    const first = options.find((def) => def.id === ids[0]);
                    if (first) setValue("name", first.name);
                  }}
                />
              </div>
              <Button type="button" variant="outline" size="sm" onClick={onRefetch} aria-label={t("ui.reload")}>
                <RotateCw />
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={!hasEvidenceSchemaSet} onClick={onAdd}>
                <Plus />
                <span>{t("ui.add")}</span>
              </Button>
            </div>
            {selectedDefs.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedDefs.map((def) => (
                  <Button key={def.id} variant="outline" size="sm" asChild>
                    <Link
                      to={v2Route.dataImportProjects.detail.$buildPath({
                        params: { id: importProjectId },
                        searchParams: { peekType, peekId: def.id },
                      })}
                    >
                      <ArrowUpRight />
                      <span>{def.name}</span>
                    </Link>
                  </Button>
                ))}
              </div>
            )}
          </div>
        );
      }}
    />
  );
}
